import { useState, useCallback, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

interface CaseReportEvidenceListProps {
  evidence: any[];
  isReadOnly?: boolean;
  involvedPersons?: any[];
  involvedVehicles?: any[];
  involvedBusinesses?: any[];
  involvedClients?: any[];
  evidenceOfOptions?: any[];
  onChange?: (evidence: any[]) => void;
  onAdd?: () => void;
  onEdit?: (item: any) => void;
  onDownload?: (attachment: any) => void;
}

export function CaseReportEvidenceList({
  evidence,
  isReadOnly = false,
  involvedPersons = [],
  involvedVehicles = [],
  involvedBusinesses = [],
  involvedClients = [],
  evidenceOfOptions = [],
  onChange,
  onAdd,
  onEdit,
  onDownload,
}: CaseReportEvidenceListProps) {
  const { theme } = useTheme();
  const rootRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(evidence?.length || 0);

  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; item: any }>({
    open: false,
    item: null,
  });

  // Watch for new evidence added - scroll to last mobile card
  useEffect(() => {
    const newLength = evidence?.length || 0;
    if (newLength > prevLengthRef.current) {
      setTimeout(() => {
        if (rootRef.current) {
          scrollToLastMobileCard(rootRef.current);
        }
      }, 0);
    }
    prevLengthRef.current = newLength;
  }, [evidence]);

  const addEvidence = useCallback(() => {
    onAdd?.();
  }, [onAdd]);

  const editEvidence = useCallback((item: any) => {
    onEdit?.(item);
  }, [onEdit]);

  const downloadAttachment = useCallback((attachment: any) => {
    onDownload?.(attachment);
  }, [onDownload]);

  const toggleExpand = useCallback((tempid: string) => {
    setExpandedIds((prev) => {
      const idx = prev.indexOf(tempid);
      if (idx >= 0) {
        return prev.filter((_, i) => i !== idx);
      }
      return [...prev, tempid];
    });
  }, []);

  const isExpanded = useCallback((tempid: string): boolean => {
    return expandedIds.includes(tempid);
  }, [expandedIds]);

  const confirmDelete = useCallback((item: any) => {
    setDeleteConfirm({ open: true, item });
  }, []);

  const doDelete = useCallback(() => {
    const item = deleteConfirm.item;
    if (item) {
      onChange?.(evidence.filter((e: any) => e.tempid !== item.tempid));
    }
    setDeleteConfirm({ open: false, item: null });
  }, [deleteConfirm.item, evidence, onChange]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, item: null });
  }, []);

  const getEvidenceOfName = useCallback((item: any): string => {
    if (item.evidenceOfName) {
      return item.evidenceOfName;
    }
    const evidenceOf = item.evidenceOf;
    if (evidenceOf && evidenceOfOptions) {
      const option = evidenceOfOptions.find(
        (o: any) => o.code === evidenceOf
      );
      if (option) {
        return option.value ?? '';
      }
    }
    return '';
  }, [evidenceOfOptions]);

  const getInvolvingName = useCallback((item: any): string => {
    if (item.involvingName) {
      return item.involvingName;
    }
    const involving = item.involving;
    if (involving) {
      const parts = involving.split('_');
      if (parts.length >= 2) {
        const id = parseInt(parts[0]);
        const type = parts[1];

        if (type === 'P' && involvedPersons) {
          const person = involvedPersons.find((p: any) => p.id === id);
          if (person) {
            return person.fullName ||
              `${person.firstName || ''} ${person.lastName || ''}`.trim() ||
              'Unknown Person';
          }
        } else if (type === 'V' && involvedVehicles) {
          const vehicle = involvedVehicles.find((v: any) => v.id === id);
          if (vehicle) {
            return `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() ||
              'Unknown Vehicle';
          }
        } else if (type === 'B' && involvedBusinesses) {
          const business = involvedBusinesses.find((b: any) => b.id === id);
          if (business) {
            return business.businessName || business.name || 'Unknown Business';
          }
        } else if (type === 'C' && involvedClients) {
          const client = involvedClients.find((c: any) => c.id === id);
          if (client) {
            return client.name || 'Unknown Client';
          }
        }
      }
    }
    return '';
  }, [involvedPersons, involvedVehicles, involvedBusinesses, involvedClients]);

  const getAttachmentCount = useCallback((item: any): number => {
    if (item.attachmentInfoList && item.attachmentInfoList.length > 0) {
      return item.attachmentInfoList.length;
    }
    if (item.attachmentList && item.attachmentList.length > 0) {
      return item.attachmentList.length;
    }
    return 0;
  }, []);

  const getFileIcon = useCallback((fileName: string): string => {
    if (!fileName) return 'fa-file';
    const ext = fileName.split('.').pop()?.toLowerCase();
    const iconMap: Record<string, string> = {
      'pdf': 'fa-file-pdf',
      'doc': 'fa-file-word',
      'docx': 'fa-file-word',
      'xls': 'fa-file-excel',
      'xlsx': 'fa-file-excel',
      'jpg': 'fa-file-image',
      'jpeg': 'fa-file-image',
      'png': 'fa-file-image',
      'gif': 'fa-file-image',
      'bmp': 'fa-file-image',
      'webp': 'fa-file-image',
      'mp4': 'fa-file-video',
      'mov': 'fa-file-video',
      'avi': 'fa-file-video',
      'mp3': 'fa-file-audio',
      'wav': 'fa-file-audio',
      'zip': 'fa-file-archive',
      'rar': 'fa-file-archive',
      'txt': 'fa-file-alt',
    };
    return iconMap[ext || ''] || 'fa-file';
  }, []);

  const isImageFile = useCallback((fileName: string): boolean => {
    if (!fileName) return false;
    const ext = fileName.split('.').pop()?.toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    return imageExtensions.indexOf(ext || '') !== -1;
  }, []);

  const getAttachmentUrl = useCallback((att: any): string => {
    return att.signedURL || att.url || '';
  }, []);

  const getImageAttachments = useCallback((item: any): any[] => {
    const attachments = item.attachmentList || item.attachmentInfoList || [];
    return attachments.filter((att: any) => {
      const fileName = att.filename || att.fileName || '';
      return isImageFile(fileName) && getAttachmentUrl(att);
    });
  }, [isImageFile, getAttachmentUrl]);

  const getFirstImageUrl = useCallback((item: any): string => {
    const images = getImageAttachments(item);
    if (images.length > 0) {
      return getAttachmentUrl(images[0]);
    }
    return '';
  }, [getImageAttachments, getAttachmentUrl]);

  const getFirstAttachment = useCallback((item: any): any | null => {
    const attachments = item.attachmentList || item.attachmentInfoList || [];
    return attachments[0] || null;
  }, []);

  return (
    <div className={'case-report-evidence-list digital-evidence-list' + (theme ? ' ' + theme : '')} ref={rootRef}>
      <div className="header">
        <h1>Digital Evidence</h1>
        {!isReadOnly && (
          <button
            onClick={addEvidence}
            className="btn-add"
            type="button"
          >
            <i className="fas fa-plus"></i> EVIDENCE
          </button>
        )}
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '80px' }}>Preview</th>
              <th style={{ width: '18%' }}>Title</th>
              <th style={{ width: '15%' }}>Evidence Of</th>
              <th style={{ width: '15%' }}>Involving</th>
              <th style={{ width: '20%' }}>File</th>
              <th style={{ width: '18%' }}>Actions</th>
            </tr>
          </thead>
          {evidence.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  No digital evidence added
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {evidence.map((item) => {
                const firstImageUrl = getFirstImageUrl(item);
                const firstAtt = getFirstAttachment(item);
                const firstAttFileName = firstAtt ? (firstAtt.filename || firstAtt.fileName) : '';

                return (
                  <tr key={item.tempid}>
                    <td className="preview-cell">
                      {firstImageUrl ? (
                        <img
                          src={firstImageUrl}
                          className="evidence-thumbnail"
                          alt="Evidence preview"
                          onClick={() => downloadAttachment({ signedURL: firstImageUrl, fileName: 'image' })}
                        />
                      ) : (
                        <div className="no-preview">
                          <i className="fa fa-file"></i>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="item-name">{item.title || 'Untitled'}</div>
                    </td>
                    <td>
                      <div className="item-type">{getEvidenceOfName(item) || '\u2014'}</div>
                    </td>
                    <td>
                      <div className="item-involving">{getInvolvingName(item) || '\u2014'}</div>
                    </td>
                    <td>
                      {firstAtt ? (
                        <a
                          href="#"
                          onClick={(e) => { e.preventDefault(); downloadAttachment(firstAtt); }}
                          className="attachment-filename clickable"
                        >
                          <i className={'fa ' + getFileIcon(firstAttFileName)}></i>
                          <span>{firstAttFileName || 'File'}</span>
                        </a>
                      ) : (
                        <div className="attachment-filename">{'\u2014'}</div>
                      )}
                    </td>
                    <td>
                      <div className="actions">
                        {!isReadOnly && (
                          <button className="btn-edit" onClick={() => editEvidence(item)}>Edit</button>
                        )}
                        {!isReadOnly && (
                          <button className="btn-delete" onClick={() => confirmDelete(item)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>

      <div className="mobile-cards">
        {evidence.length === 0 ? (
          <div className="empty-state">
            <p>No digital evidence added</p>
          </div>
        ) : (
          evidence.map((item) => {
            const attachments = item.attachmentList || item.attachmentInfoList || [];

            return (
              <div key={'mobile-' + item.tempid} className="card">
                <div className="card-header">
                  <div className="card-header-content">
                    <div className="card-item-name">{item.title || 'Untitled'}</div>
                    {getEvidenceOfName(item) && (
                      <span className="type-badge">
                        {getEvidenceOfName(item)}
                      </span>
                    )}
                  </div>
                  <div className="card-header-actions">
                    {!isReadOnly && (
                      <button className="btn-edit-sm" onClick={() => editEvidence(item)}>Edit</button>
                    )}
                    {!isReadOnly && (
                      <button className="btn-delete-sm" onClick={() => confirmDelete(item)}>Delete</button>
                    )}
                  </div>
                </div>

                <div className="card-body">
                  {getInvolvingName(item) && (
                    <div className="card-field">
                      <div className="card-field-label">Involving</div>
                      <div className="card-field-value">{getInvolvingName(item)}</div>
                    </div>
                  )}
                  {getAttachmentCount(item) > 0 && (
                    <div className="card-field">
                      <div className="card-field-label">File</div>
                      {attachments.map((att: any) => {
                        const attFileName = att.filename || att.fileName;
                        return (
                          <a
                            key={att.id}
                            href="#"
                            onClick={(e) => { e.preventDefault(); downloadAttachment(att); }}
                            className="attachment-link-mobile attachment-with-preview"
                          >
                            {isImageFile(attFileName) ? (
                              <img
                                src={getAttachmentUrl(att)}
                                className="attachment-thumbnail-mobile"
                                alt={attFileName}
                              />
                            ) : (
                              <i className={'fa ' + getFileIcon(attFileName)}></i>
                            )}
                            <span>{attFileName || 'Download'}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <ConfirmModal
        open={deleteConfirm.open}
        title="Remove Digital Evidence?"
        messageHtml="Are you sure you want to remove this digital evidence from the report?"
        confirmText="Remove"
        confirmClass="danger"
        onConfirm={doDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

(window as any).CaseReportEvidenceListReact = CaseReportEvidenceList;
